import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../API';
import { BLOCK_SETUP_ROUTE } from '../../constants/setupRoadmapSteps';
import styles from './ProtocolSelectionModal.module.css';

/**
 * Pick an existing sectioning protocol (Case Builder load / wizard “show all”).
 * Does not create protocols — authoring lives on Block Setup.
 */
export default function ProtocolPickerModal({
    isOpen,
    onClose,
    onSelect,
    selectedProtocolId = null,
    title = 'Select Sectioning Protocol',
}) {
    const { data: protocolsResponse, isLoading } = useQuery({
        queryKey: ['bdsa-protocol'],
        queryFn: () => api.get('/bdsa/protocol'),
        enabled: isOpen,
        staleTime: 5 * 60 * 1000,
    });

    const protocols = protocolsResponse?.status === 200 && Array.isArray(protocolsResponse.data)
        ? protocolsResponse.data
        : [];

    if (!isOpen) return null;

    const handleSelect = (protocol) => {
        onSelect?.(protocol);
        onClose?.();
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>{title}</h3>
                    <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {isLoading ? (
                        <div>Loading protocols...</div>
                    ) : protocols.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>No sectioning protocols yet.</p>
                            <p>
                                Create protocols and their blocks in{' '}
                                <Link to={BLOCK_SETUP_ROUTE} onClick={onClose}>
                                    Block Setup
                                </Link>
                                , then come back to select one.
                            </p>
                        </div>
                    ) : (
                        <div className={styles.protocolsList}>
                            {protocols.map((protocol) => (
                                <button
                                    key={protocol._id}
                                    type="button"
                                    className={`${styles.protocolItem} ${selectedProtocolId === protocol._id ? styles.selected : ''}`}
                                    onClick={() => handleSelect(protocol)}
                                >
                                    <div className={styles.protocolName}>{protocol.name}</div>
                                    {protocol.description ? (
                                        <div className={styles.protocolDescription}>{protocol.description}</div>
                                    ) : null}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.modalFooter}>
                    <Link
                        className={styles.manageLink}
                        to={BLOCK_SETUP_ROUTE}
                        onClick={onClose}
                    >
                        Manage protocols in Block Setup
                    </Link>
                    <button type="button" className={styles.cancelButton} onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}
